import { useState, useEffect } from "react";
import { useTonWallet } from "@tonconnect/ui-react";
import { rpcCall, subscribe } from "../ws/client";
import { toast } from "./ui/Toast";
import { 
    checkPendingTransactions, 
    retryTransactionVerification,
    PendingTransaction 
} from "../utils/txRecovery";
import "../styles/PendingTransactions.scss";


export default function PendingTransactions(): JSX.Element {
    const wallet = useTonWallet();
    const [pending, setPending] = useState<PendingTransaction[]>([]);
    const [loading, setLoading] = useState(false);
    const [retrying, setRetrying] = useState<Record<string, boolean>>({});
    const [isExpanded, setIsExpanded] = useState(true);


    const loadPending = async () => {
        if (!wallet?.account?.address) return;

        setLoading(true);
        try {
            const transactions = await checkPendingTransactions(wallet.account.address);
            setPending(transactions);
        } catch (error) {
            console.error("Failed to load pending transactions:", error);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        loadPending();
        
        // Subscribe to transaction updates
        const unsubscribe = subscribe("transaction.updated", () => {
            loadPending();
        });
        
        return () => unsubscribe();
    }, [wallet]);


    const handleRetry = async (pendingTxId: string) => {
        setRetrying(prev => ({ ...prev, [pendingTxId]: true }));
        
        try {
            const success = await retryTransactionVerification(pendingTxId);
            
            if (success) {
                toast.success("✅ Транзакция подтверждена!");
                setPending(prev => prev.filter(tx => tx._id !== pendingTxId));
                await loadPending();
            } else {
                toast.warning("⏳ Транзакция еще не найдена в блокчейне. Попробуйте позже.");
            }
        } catch (error: any) {
            toast.error(error.message || "Ошибка при проверке транзакции");
        } finally {
            setRetrying(prev => ({ ...prev, [pendingTxId]: false }));
        }
    };


    const handleCancel = async (pendingTxId: string) => {
        if (!confirm("Вы уверены? Если транзакция уже отправлена, её НЕ ОТМЕНИТЬ.")) {
            return;
        }

        try {
            const response = await rpcCall<
                { pending_tx_id: string },
                { success: boolean; error?: string }
            >("tx.cancel_pending", {
                pending_tx_id: pendingTxId
            });

            if (response.result?.success) {
                toast.info("Ожидающая транзакция удалена из списка");
                await loadPending();
            } else {
                const errMsg = response.result?.error || response.error?.message || "Не удалось отменить транзакцию";
                toast.error(errMsg);
                console.error("Cancel failed:", response);
            }
        } catch (error: any) {
            toast.error(error.message || "Ошибка при отмене");
        }
    };


    const getTypeLabel = (type: string): string => {
        const labels: Record<string, string> = {
            "lottery_ticket": "🎟 Билет лотереи",
            "escrow_deposit": "🔒 Escrow депозит",
            "jetton_purchase": "💰 Покупка жетонов",
            "coinflip_bet": "🎰 Ставка Coin Flip"
        };
        return labels[type] || type;
    };


    const getStatusLabel = (status: string): { text: string; color: string } => {
        switch (status) {
            case "pending":
                return { text: "⏳ Ожидание", color: "#ffc107" };
            case "sent":
                return { text: "📤 Отправлена", color: "#17a2b8" };
            case "confirmed":
                return { text: "✅ Подтверждена", color: "#28a745" };
            case "verified":
                return { text: "✅ Подтверждена", color: "#28a745" };
            case "failed":
                return { text: "❌ Ошибка", color: "#dc3545" };
            case "cancelled":
                return { text: "🚫 Отменена", color: "#6c757d" };
            default:
                return { text: status, color: "#6c757d" };
        }
    };


    const formatTime = (isoDate: string): string => {
        const date = new Date(isoDate);
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const minutes = Math.floor(diff / 60000);
        
        if (minutes < 1) return "только что";
        if (minutes < 60) return `${minutes} мин назад`;
        
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours} ч назад`;
        
        const days = Math.floor(hours / 24);
        return `${days} дн назад`;
    };


    if (!wallet) return null;
    if (pending.length === 0) return null;


    return (
        <div className={`pending-transactions ${isExpanded ? "expanded" : "collapsed"}`}>
            <div className="pending-header" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="header-left">
                    <h3>⏳ Ожидающие транзакции</h3>
                    <span className="count">{pending.length}</span>
                </div>
                <button className="toggle-btn">
                    {isExpanded ? "▼" : "▶"}
                </button>
            </div>

            {isExpanded && (
                <div className="pending-content">
                    {loading && (
                        <div className="loading">
                            <div className="spinner" />
                            <span>Загрузка...</span>
                        </div>
                    )}

                    {!loading && (
                        <>
                            <div className="info-banner">
                                <p>
                                    💡 Эти транзакции были отправлены, но ещё не подтверждены.
                                    Система автоматически проверяет их каждые 30 секунд.
                                </p>
                            </div>

                            <div className="transactions-list">
                                {pending.map(tx => {
                                    const statusInfo = getStatusLabel(tx.status);
                                    const isOld = new Date().getTime() - new Date(tx.created_at).getTime() > 5 * 60 * 1000; // > 5 min

                                    return (
                                        <div key={tx._id} className={`transaction-card ${tx.status}`}>
                                            <div className="tx-header">
                                                <span className="tx-type">{getTypeLabel(tx.type)}</span>
                                                <span 
                                                    className="tx-status"
                                                    style={{ color: statusInfo.color }}
                                                >
                                                    {statusInfo.text}
                                                </span>
                                            </div>

                                            <div className="tx-body">
                                                {tx.metadata && (
                                                    <div className="tx-details">
                                                        {tx.metadata.jetton_amount && (
                                                            <div className="detail-item">
                                                                <span>Сумма:</span>
                                                                <strong>
                                                                    {tx.metadata.jetton_amount} {tx.metadata.jetton_symbol || ""}
                                                                </strong>
                                                            </div>
                                                        )}
                                                        {tx.metadata.ton_cost && (
                                                            <div className="detail-item">
                                                                <span>Стоимость:</span>
                                                                <strong>{tx.metadata.ton_cost} TON</strong>
                                                            </div>
                                                        )}
                                                        {tx.metadata.ticket_count && (
                                                            <div className="detail-item">
                                                                <span>Билетов:</span>
                                                                <strong>{tx.metadata.ticket_count}</strong>
                                                            </div>
                                                        )}
                                                        {tx.metadata.bet_amount && (
                                                            <div className="detail-item">
                                                                <span>Ставка:</span>
                                                                <strong>{tx.metadata.bet_amount} TON</strong>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="tx-time">
                                                    {formatTime(tx.created_at)}
                                                    {isOld && (
                                                        <span className="warning-badge">⚠️ Долго ожидает</span>
                                                    )}
                                                </div>

                                                {tx.tx_boc && (
                                                    <div className="tx-hash">
                                                        <span>BOC:</span>
                                                        <code>{tx.tx_boc.slice(0, 16)}...</code>
                                                    </div>
                                                )}
                                                
                                                {tx.refund_tx_hash && tx.refund_amount && (
                                                    <div className="tx-refund">
                                                        <span>💰 Возврат средств:</span>
                                                        <strong>{tx.refund_amount} TON</strong>
                                                        <div className="refund-note">
                                                            (удержаны: комиссия платформы 5% + газ 0.05 TON)
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="tx-actions">
                                                {tx.status === "pending" || tx.status === "sent" ? (
                                                    <>
                                                        <button
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() => handleRetry(tx._id)}
                                                            disabled={retrying[tx._id]}
                                                        >
                                                            {retrying[tx._id] ? "⏳ Проверка..." : "🔄 Проверить"}
                                                        </button>
                                                        
                                                        {isOld && (
                                                            <button
                                                                className="btn btn-sm btn-secondary"
                                                                onClick={() => handleCancel(tx._id)}
                                                            >
                                                                ✕ Убрать
                                                            </button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <button
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() => handleCancel(tx._id)}
                                                    >
                                                        ✓ Понятно, убрать
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <button 
                                className="refresh-btn"
                                onClick={loadPending}
                                disabled={loading}
                            >
                                {loading ? "⏳" : "🔄"} Обновить
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
